'use client';
import { useLocale, useTranslations } from 'next-intl';
import type React from 'react';
import { Fragment, useMemo } from 'react';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';
import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';

import { getAgeRatingIcon } from '@/helpers/movie-helper';
import { getShowingPropertiesDimension, getShowingTechnologies } from '@/helpers/showing-helper';
import { formatDuration } from '@/helpers/time-helper';
import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { useAgeRatingText } from '@cineamo/ui/src/helpers/movie-helper';
import { getShowingLanguages } from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { IconAudio16 } from '@cineamo/ui/src/icons/ic-audio';
import { IconCinemaScreen16 } from '@cineamo/ui/src/icons/ic-cinemaScreen';
import { IconClock16 } from '@cineamo/ui/src/icons/ic-clock';
import { GretaIcon } from '@cineamo/ui/src/icons/ic-greta';
import { IconLocation16 } from '@cineamo/ui/src/icons/ic-location';
import { IconStar16 } from '@cineamo/ui/src/icons/ic-star';
import { IconSubtitle16 } from '@cineamo/ui/src/icons/ic-subtitle';
import { IconWheelchair16 } from '@cineamo/ui/src/icons/ic-wheelchair';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { usePageName } from '@/utils/tracking-helper';

const ShowingInfoModalContent: React.FC<{
    showing: ShowingDto;
    content?: CachedContentData;
    specials: ShowingTagDto[];
    showShowingDetails?: boolean;
    cinemaRoom: CinemaRoomDto;
}> = ({ showing, content, specials, showShowingDetails, cinemaRoom }) => {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();
    const locale = useLocale();
    const pageName = usePageName();
    const { track } = useAnalytics();
    const cinema = domainConfiguration.cinema;

    const showingTechnologies = getShowingTechnologies(showing);
    const showingDimension = useMemo(() => getShowingPropertiesDimension(showing), [showing]);
    const movieDirector = content?.director ?? '';
    const actors = content?.actors ?? [];
    const genres = content?.genres?.slice(0, 3).map((genre) => t(`genres.tmdb-genre-${genre.id}`)) ?? [];

    const ageRating = content?.ageRating;
    const ageRatingIcon = getAgeRatingIcon(ageRating, domainConfiguration.cinema.countryCode, {
        movieId: content?.movieId
    });
    const ageRatingText = useAgeRatingText(ageRating, domainConfiguration.cinema.countryCode);
    const showingLanguages = getShowingLanguages(showing, locale, {
        languageFormat: 'long',
        languages: t.raw('languages')
    });
    const runtime = formatDuration(content?.runtime, t);
    const gretaIconVariant = domainConfiguration.websiteConfiguration?.gretaIconVariant;
    const movieAccessibilityFeatures = content.movieAccessibility
        ? [
              content.movieAccessibility.hasClosedCaption && t('greta.closed-caption'),
              content.movieAccessibility.hasAudioDescription && t('greta.audio-description'),
              content.movieAccessibility.hasSoundAmplification && t('greta.sound-amplification')
          ].filter(Boolean)
        : [];
    return (
        <div className="flex flex-col space-y-16">
            {showing.highlightText && (
                <div
                    className="py-1.5 px-8 rounded-4"
                    style={{ backgroundColor: withOpacity(domainConfiguration.style.primaryColor, 0.2) }}>
                    <h3 className="text-body-bold-14 flex row items-center space-x-6">
                        <IconStar16 />
                        <span>{t('showing-info.highlight')}</span>
                    </h3>
                    <div className="text-body-14">{showing.highlightText}</div>
                </div>
            )}

            <div className="flex row px-8 space-x-8">
                <div className="w-1/2">
                    <h3 className="text-body-bold-14 flex row items-center space-x-6">
                        <IconLocation16 />
                        <span>{t('showing-info.cinema')}</span>
                    </h3>
                    <div className="text-body-14">
                        <Link
                            href={'/info'}
                            className="underline">
                            {cinema.name}
                        </Link>
                    </div>
                </div>

                <div className="w-1/2">
                    <h3 className="text-body-bold-14 flex row items-center space-x-6">
                        <IconCinemaScreen16 />
                        <span>{t('showing-info.cinema-hall')}</span>
                    </h3>
                    <div className="text-body-14">
                        {cinemaRoom ? (
                            <>
                                <Link
                                    href={{
                                        pathname: '/info',
                                        query: { roomId: showing.cinemaRoomId }
                                    }}
                                    onClick={() => {
                                        track('Cinema Room Details Opened', {
                                            cinemaRoomId: cinemaRoom.id,
                                            cinemaRoomName: cinemaRoom.name,
                                            page: pageName
                                        });
                                    }}
                                    className="underline">
                                    {cinemaRoom.name}
                                </Link>
                                {cinemaRoom.seatCount && (
                                    <>
                                        &nbsp;(
                                        {t('showing-info.seat-count', { seats: cinemaRoom.seatCount })})
                                    </>
                                )}
                            </>
                        ) : (
                            <>&ndash;</>
                        )}
                    </div>
                </div>
            </div>

            {cinemaRoom?.hasWheelchairSpaces && (
                <div className="px-8">
                    <h3 className="text-body-bold-14 flex row items-center space-x-6">
                        <IconWheelchair16 />
                        <span>{t('showing-info.wheelchair-accessible')}</span>
                    </h3>
                </div>
            )}

            <div className="flex row px-8 space-x-8">
                <div className="w-1/2">
                    <h3 className="text-body-bold-14 flex row items-center space-x-6">
                        <IconAudio16 />
                        <span>{t('showing-info.audio')}</span>
                    </h3>
                    <div className="text-body-14">{showingLanguages.audioLanguage ?? <>&ndash;</>}</div>
                </div>

                <div className="w-1/2">
                    <h3 className="text-body-bold-14 flex row items-center space-x-6">
                        <IconSubtitle16 />
                        <span>{t('showing-info.subtitle')}</span>
                    </h3>
                    <div className="text-body-14">{showingLanguages.subtitleLanguage ?? <>&ndash;</>}</div>
                </div>
            </div>

            {showShowingDetails && (
                <div className="flex row px-8 space-x-8">
                    <div className="w-1/2">
                        <h3 className="text-body-bold-14 flex row items-center space-x-6">
                            <IconClock16 />
                            <span>{t('showing-info.duration')}</span>
                        </h3>
                        <div className="text-body-14">{runtime}</div>
                    </div>

                    <div className="w-1/2">
                        <h3 className="text-body-bold-14 flex row items-center space-x-6">
                            <span>{t('showing-info.fsk')}</span>
                        </h3>
                        <div className="text-body-14 flex row space-x-8 leading-[24px]">
                            {ageRatingIcon || ageRatingText ? (
                                <>
                                    {ageRatingIcon} <span>{ageRatingText}</span>
                                </>
                            ) : (
                                '-'
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="px-8">
                <h3 className="text-body-bold-14">{t('showing-info.dimension')}</h3>
                <div className="text-body-14">{showingDimension}</div>
            </div>

            <div className="px-8">
                <h3 className="text-body-bold-14">{t('showtimes.showing-info.technology.title')}</h3>
                <div className="text-body-14">
                    {showingTechnologies.length > 0 ? (
                        showingTechnologies
                            .map((technology) => {
                                const str = `showtimes.showing-info.technology.${technology}`;
                                return t(str);
                            })
                            .join(', ')
                    ) : (
                        <>&ndash;</>
                    )}
                </div>
            </div>

            <div className="px-8">
                <h3 className="text-body-bold-14">{t('showing-info.special')}</h3>
                <div className="text-body-14">
                    {specials.length > 0 ? (
                        specials.map((special, i, self) => (
                            <Fragment key={`${showing.id}-special-${special.id}`}>
                                <Link
                                    href={`/specials/${special.id}`}
                                    locale={locale}
                                    className="underline">
                                    {special.name}
                                </Link>
                                {i < self.length - 1 && ', '}
                            </Fragment>
                        ))
                    ) : (
                        <>&ndash;</>
                    )}
                </div>
            </div>

            <div className="px-8">
                <h3 className="text-body-bold-14 flex row items-center space-x-6">
                    <GretaIcon
                        variant={gretaIconVariant}
                        width="16"
                        height="16"
                        style={{ width: 16, height: 16 }}
                    />
                    <span>{t('greta.accessibility')}</span>
                </h3>
                <div className="text-body-14">
                    {!content.movieAccessibility ? (
                        t('greta.no-greta-info')
                    ) : movieAccessibilityFeatures.length > 0 ? (
                        t('greta.accessibility-features', {
                            features: movieAccessibilityFeatures.join(', ')
                        })
                    ) : movieAccessibilityFeatures.length === 0 ? (
                        t('greta.no-accessibility-features')
                    ) : (
                        <>&ndash;</>
                    )}
                </div>
            </div>

            {showShowingDetails && (
                <div className="px-8">
                    <h3 className="text-body-bold-14">{t('showing-info.director')}</h3>
                    <div className="text-body-14">{movieDirector.length > 0 ? movieDirector : <>&ndash;</>}</div>
                </div>
            )}

            {showShowingDetails && (
                <div className="px-8">
                    <h3 className="text-body-bold-14">{t('showing-info.actors')}</h3>
                    <div className="text-body-14">{actors.length > 0 ? actors.join(', ') : <>&ndash;</>}</div>
                </div>
            )}

            {showShowingDetails && (
                <div className="px-8">
                    <h3 className="text-body-bold-14">{t('showing-info.genres')}</h3>
                    <div className="text-body-14">{genres.length > 0 ? genres.join(', ') : <>&ndash;</>}</div>
                </div>
            )}
        </div>
    );
};

export default ShowingInfoModalContent;
